import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackLink from "../../../../components/BackLink/BackLink";
import { useRunReport } from "../../../../hooks/useRunReport";
import { useBusinessProfile } from "../../../../hooks/useBusinessProfile";
import { useReportPreferences } from "../../../../hooks/useReportPreferences";
import type { ReportPeriod } from "./types";
import { DEFAULT_PERIOD } from "./constants";
import { periodToDateRange, fmtDateDMY } from "./utils";
import QuickReportControls from "./components/QuickReportControls";
import QuickReportHeader from "./components/QuickReportHeader";
import QuickReportToolbar from "./components/QuickReportToolbar";
import QuickReportColumnHeader from "./components/QuickReportColumnHeader";
import QuickReportTable from "./components/QuickReportTable";
import SaveReportModal from "./components/SaveReportModal";
import SendReportModal from "./components/SendReportModal";
import PrintReportModal from "./components/PrintReportModal";
import ScheduleReportModal from "./components/ScheduleReportModal";
import ShareReportModal from "./components/ShareReportModal";
import AddToGroupModal from "./components/AddToGroupModal";
import AddToManagementReportsModal from "./components/AddToManagementReportsModal";
import CustomizeDrawer from "./components/CustomizeDrawer";
import EmptyState from "./components/EmptyState";
import type { ExportFormat } from "./components/ExportMenu";
import type { Density, Zoom } from "./components/CompactViewMenu";

const DENSITY_KEY = "novala.runReport.density";
const ZOOM_KEY = "novala.runReport.zoom";

function loadDensity(): Density {
  try {
    const v = window.localStorage.getItem(DENSITY_KEY);
    if (v === "compact" || v === "normal" || v === "cozy" || v === "comfort") return v;
  } catch {}
  return "compact";
}
function loadZoom(): Zoom {
  try {
    const n = Number(window.localStorage.getItem(ZOOM_KEY));
    if ([75, 90, 100, 125, 150].includes(n)) return n as Zoom;
  } catch {}
  return 100;
}

export default function RunReportPage() {
  const navigate = useNavigate();
  const { accountId = "default" } = useParams<{ accountId: string }>();

  const [period, setPeriod] = useState<ReportPeriod>(DEFAULT_PERIOD);
  const initialRange = useMemo(() => periodToDateRange(DEFAULT_PERIOD), []);
  const [from, setFrom] = useState<string>(initialRange.from);
  const [to, setTo] = useState<string>(initialRange.to);
  const [saveOpen, setSaveOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [addToMgmtOpen, setAddToMgmtOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [density, setDensityState] = useState<Density>(loadDensity);
  const [zoom, setZoomState] = useState<Zoom>(loadZoom);
  const [groupsExpanded, setGroupsExpanded] = useState<boolean>(true);

  const customFrom = period === "custom" ? from : undefined;
  const customTo = period === "custom" ? to : undefined;

  const { data, loading, error, refetch } = useRunReport({ accountId, period, customFrom, customTo });
  const { data: business } = useBusinessProfile();
  const { prefs, save: savePrefs, reset: resetPrefs } = useReportPreferences();

  const shareUrl = useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "https://app.getnovala.com";
    const token = `sh-${Date.now().toString(36)}`;
    return `${base}/accounting/chart-of-accounts/${accountId}/report?shared=${token}&period=${period}${period === "custom" ? `&from=${from}&to=${to}` : ""}`;
  }, [accountId, period, from, to]);

  const handlePeriodChange = useCallback((p: ReportPeriod) => {
    setPeriod(p);
    if (p !== "custom") {
      const r = periodToDateRange(p);
      setFrom(r.from); setTo(r.to);
    }
  }, []);
  const handleFromChange = useCallback((iso: string) => { setFrom(iso); setPeriod("custom"); }, []);
  const handleToChange = useCallback((iso: string) => { setTo(iso); setPeriod("custom"); }, []);
  const handleBack = useCallback(() => { navigate("/accounting/chart-of-accounts"); }, [navigate]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    refetch();
    setLastRefreshedAt(Date.now());
    window.setTimeout(() => setRefreshing(false), 450);
  }, [refetch]);

  const handleDensityChange = useCallback((d: Density) => {
    setDensityState(d);
    try { window.localStorage.setItem(DENSITY_KEY, d); } catch {}
  }, []);
  const handleZoomChange = useCallback((z: Zoom) => {
    setZoomState(z);
    try { window.localStorage.setItem(ZOOM_KEY, String(z)); } catch {}
  }, []);

  const downloadFile = useCallback((format: ExportFormat) => {
    if (!data) return;
    if (format === "pdf") { setPrintOpen(true); return; }
    const headers = ["Distribution account", "Date", "Type", "Ref no", "Payee", "Description", "Account", "Cleared", "Amount", "Balance"];
    const buildRow = (r: any) => [r.distributionAccount, fmtDateDMY(r.date), r.type, r.refNo, r.payee, r.description, r.accountName, r.cleared, r.amount.toFixed(2), r.balance.toFixed(2)];
    let rows: (string | number)[][] = [];
    if (format === "excel-grouped") {
      data.groups.forEach((g) => {
        rows.push([`${g.label} (${g.rows.length})`, "", "", "", "", "", "", "", "", ""]);
        g.rows.forEach((r) => rows.push(buildRow(r)));
        rows.push([`Total for ${g.label}`, "", "", "", "", "", "", "", g.subtotal.toFixed(2), g.endBalance.toFixed(2)]);
      });
      rows.push(["Total", "", "", "", "", "", "", "", data.total.toFixed(2), data.endBalance.toFixed(2)]);
    } else {
      rows = data.groups.flatMap((g) => g.rows.map(buildRow));
    }
    const sep = format === "csv" ? "," : "\t";
    const ext = format === "csv" ? ".csv" : ".xlsx";
    const mime = format === "csv" ? "text/csv" : "application/vnd.ms-excel";
    const content = [headers, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(sep)).join("\n");
    const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `account-quick-report-${data.accountName.replace(/\s+/g, "-").toLowerCase()}${ext}`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }, [data]);

  return (
    <div style={{ maxWidth: "100%", margin: 0, padding: "0 32px 90px" }}>
      <div className="sticky top-0 z-20 bg-white -mx-4 md:-mx-8 px-4 md:px-8 pt-2 pb-0 no-print">
        <BackLink to="/accounting/chart-of-accounts" label="Back to chart of accounts" />
        <QuickReportControls
          period={period} from={from} to={to}
          onPeriodChange={handlePeriodChange} onFromChange={handleFromChange} onToChange={handleToChange}
          onCustomize={() => setCustomizeOpen(true)} onSaveAs={() => setSaveOpen(true)}
        />
        {data && (
          <>
            <QuickReportHeader businessName={business?.name ?? ""} accountName={data.accountName} from={data.from} to={data.to} prefs={prefs} />
            <QuickReportToolbar
              onRefresh={handleRefresh}
              onEmail={() => setEmailOpen(true)}
              onPrint={() => setPrintOpen(true)}
              onExport={downloadFile}
              onScheduleReport={() => setScheduleOpen(true)}
              onShareReport={() => setShareOpen(true)}
              onAddToGroup={() => setAddToGroupOpen(true)}
              onAddToManagementReports={() => setAddToMgmtOpen(true)}
              lastRefreshedAt={lastRefreshedAt}
              refreshing={refreshing}
              density={density}
              zoom={zoom}
              groupsExpanded={groupsExpanded}
              onDensityChange={handleDensityChange}
              onZoomChange={handleZoomChange}
              onGroupsExpandedChange={setGroupsExpanded}
            />
            {data.groups.length > 0 && <QuickReportColumnHeader />}
          </>
        )}
      </div>

      <div className="print-area relative" style={{ fontSize: `${zoom}%` }}>
        {refreshing && (
          <div className="absolute inset-0 bg-white/60 z-10 pointer-events-none" aria-hidden="true" />
        )}
        {loading ? (
          <div className="py-14 text-center text-ink3 text-sm">Loading report...</div>
        ) : error ? (
          <div className="py-14 text-center">
            <div className="text-ink font-semibold text-base mb-1">Could not load report</div>
            <div className="text-ink3 text-sm mb-4">{error}</div>
            <button type="button" onClick={refetch} className="h-9 px-4 border border-ink rounded-[10px] text-[12.5px] font-semibold text-white bg-ink">Try again</button>
          </div>
        ) : data ? (
          data.groups.length === 0 ? (
            <EmptyState onChangePeriod={() => handlePeriodChange("all-dates")} onBack={handleBack} />
          ) : (
            <QuickReportTable data={data} prefs={prefs} density={density} groupsExpanded={groupsExpanded} />
          )
        ) : null}

        {data && (prefs.showDatePrepared || prefs.showTimePrepared) && (
          <div className={`pt-4 text-[11px] text-ink3 ${prefs.footerAlignment === "left" ? "text-left" : prefs.footerAlignment === "center" ? "text-center" : "text-right"}`}>
            {prefs.showDatePrepared && <>Prepared {fmtDateDMY(new Date().toISOString())}</>}
            {prefs.showDatePrepared && prefs.showTimePrepared && " "}
            {prefs.showTimePrepared && <>{new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</>}
          </div>
        )}
      </div>

      {data && (
        <>
          <SaveReportModal open={saveOpen} onClose={() => setSaveOpen(false)} accountId={accountId} accountName={data.accountName} period={period} customFrom={customFrom} customTo={customTo} />
          <SendReportModal open={emailOpen} onClose={() => setEmailOpen(false)} data={data} businessName={business?.name ?? ""} />
          <PrintReportModal open={printOpen} onClose={() => setPrintOpen(false)} data={data} businessName={business?.name ?? ""} />
          <ScheduleReportModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} accountName={data.accountName} />
          <ShareReportModal open={shareOpen} onClose={() => setShareOpen(false)} shareUrl={shareUrl} />
          <AddToGroupModal open={addToGroupOpen} onClose={() => setAddToGroupOpen(false)} accountId={accountId} accountName={data.accountName} />
          <AddToManagementReportsModal open={addToMgmtOpen} onClose={() => setAddToMgmtOpen(false)} accountId={accountId} accountName={data.accountName} />
          <CustomizeDrawer open={customizeOpen} onClose={() => setCustomizeOpen(false)} prefs={prefs} onSave={savePrefs} onReset={resetPrefs} />
        </>
      )}
    </div>
  );
}